/* eslint-disable react-hooks/exhaustive-deps */
import { bannerStates, feedblockTypes, itemTypes } from '@constants/index';
import useInfiniteFetch from '@hooks/useInfiniteFetch';
import {
  isBannerBlock,
  isBatchFeed,
  isCourseFeedBlock,
  isEducatorFeed,
  isSubjectFeed,
  isTestSeriesFeed,
  isTopicFeed
} from '@utils/feedTypeHelper';
import { processCourse } from '@api/processors/course';
import { lessonModel } from '../course/utils';

const getFormatTopology = (feedBlock = {}) => {
  return {
    ...feedBlock,
    topologies: feedBlock?.data?.map((topology) => ({
      ...topology,
      plusCourseCount: topology.count,
      coursesCount:
        topology.coursesCount ||
        topology.itemCount ||
        topology.count ||
        topology.courseCount ||
        0,
      upcomingCoursesCount: topology.upcomingCount || 0,
      enrolledCoursesCount: topology.enrolledCount || 0,
      permalink: '' // need to handle
    }))
  };
};

const getProcessedSessionInfo = (sessionInfo = {}) => {
  return {
    ...sessionInfo,
    relative_link: sessionInfo.relativeLink,
    has_watched: sessionInfo.hasWatched,
    has_attended: sessionInfo.hasAttended,
    start_time: sessionInfo.startTime,
    course_info: sessionInfo.courseInfo,
    live_at: sessionInfo.liveAt
  };
};

const getCarouselFeed = (feedBlock = {}) => {
  return {
    ...feedBlock,
    carouselFeed: feedBlock?.data?.map((data) => {
      const course = [
        bannerStates.LEGENDS,
        bannerStates.TEST_SERIES,
        bannerStates.GENERIC,
        bannerStates.COMBAT,
        bannerStates.RENEWAL
      ].includes(data.bannerType)
        ? data?.sessionInfo
        : data?.sessionInfo?.courseInfo;
      return {
        course: course ? processCourse(course) : course,
        sessionData: data.sessionInfo
          ? getProcessedSessionInfo(data.sessionInfo)
          : data.sessionInfo,
        bannerDetails: {
          uid: data.uid,
          title: data.title,
          description: data.description,
          type: data.bannerType,
          bannerPhoto: data.bannerPhoto,
          clickURL: data.clickUrl
        }
      };
    })
  };
};

const isBatch = (entity) => !!entity.batchTemplate;

const processClassFeed = (courseData) => {
  if (courseData.type === itemTypes.POST) return lessonModel(courseData);
  if (!isBatch(courseData)) {
    const processData = processCourse(courseData);
    return {
      ...processData,
      nextTrack: processData.nextTrack
        ? lessonModel(processData.nextTrack)
        : {},
      previousTrack: processData.previousTrack
        ? lessonModel(processData.previousTrack)
        : {}
    };
  }
  return null;
};

const getProcessClassFeedData = (feed) => {
  return {
    ...feed,
    courses: (feed.data || []).map(processClassFeed).filter((obj) => obj),
    batches: feed.data?.filter?.(isBatch) || []
  };
};
const getProcessTesTestSeriesFeedData = (feed) => {
  return {
    ...feed,
    data: feed.data?.map((testSeries) => {
      const sessionDetails = { ...(testSeries?.sessionDetails || {}) };
      return {
        ...testSeries,
        view_type: testSeries.viewType,
        start_time: testSeries.startTime,
        num_questions: testSeries.numQuestions,
        test_series_slug: testSeries.testSeriesSlug,
        is_special: testSeries.isSpecial,
        session_details: {
          ...sessionDetails,
          attempt_count: sessionDetails?.attemptCount,
          last_attempt: sessionDetails?.lastAttempt,
          is_ongoing: sessionDetails?.isOngoing
        }
      };
    }),
    courses: (feed.data || []).map(processClassFeed).filter((obj) => obj),
    batches: feed.data?.filter?.(isBatch) || []
  };
};

const processEducators = (feed = {}) => {
  return {
    ...feed,
    users: feed.data.map((educatorData) => ({
      ...educatorData,
      name: `${educatorData?.firstName} ${educatorData?.lastName}`,
      username: educatorData?.username?.toLowerCase() || null,
      thumbnail: educatorData?.avatar_v1 || educatorData?.avatar || null,
      fallbackAvatar: educatorData?.avatar || null,
      totalWatchMins: educatorData?.totalWatchTime
        ? Math.round(educatorData?.totalWatchTime / 60)
        : 0,
      description: educatorData?.bio || null,
      leaderboardMetaInfo: {
        ...(educatorData?.leaderboardMetaInfo || {}),
        value_props: (educatorData?.leaderboardMetaInfo?.valueProps || []).map(
          (valueProp) => {
            return {
              ...(valueProp || {}),
              display_string: valueProp?.displayString
            };
          }
        )
      }
    }))
  };
};

const getProcessedBatchFeed = (feed) => {
  return {
    ...feed,
    batches: (feed.data || []).map((batch) => ({
      ...batch,
      trialClasses: batch.trialInfo || {}
    }))
  };
};

const getProcessedDymTypeFeed = (feed = {}) => {
  return {
    ...feed,
    suggestions: (feed.data || []).map((suggestion) => suggestion.suggestion)
  };
};

const getProcessedRelatedSearchesTypeFeed = (feed = {}) => {
  return {
    ...feed,
    relatedSearches: (feed.data || []).map((query) => query.query)
  };
};
const getProcessedCombatTypeFeed = (feed = {}) => {
  return {
    ...feed,
    combatData: (feed.data || []).map((combat) => ({
      ...combat,
      user: combat.author
    }))
  };
};
const isExternalSubscription = (type) => {
  return (
    type === feedblockTypes.EXT_SUBSCRIPTION_PREACTIVATION ||
    type === feedblockTypes.EXT_SUBSCRIPTION_POSTACTIVATION
  );
};

const getProcessedExternalSubscription = (feed = {}) => {
  const extraBlockinfo = {
    ...(feed.extraBlockInfo || {})
  };

  const externalApp = {
    ...(extraBlockinfo.externalApp || {})
  };

  return {
    ...feed,
    extraBlockinfo: {
      ...extraBlockinfo,
      activation_url: extraBlockinfo.activationUrl,
      external_app: {
        ...externalApp,
        display_name: externalApp.displayName,
        web_url: externalApp.webUrl
      }
    }
  };
};

const getProcessedSubjects = (feed = {}) => {
  return {
    ...feed,
    data: feed.data.map((subject) => ({
      ...subject,
      data: subject.data.map((info) => ({
        ...info,
        icon_colour: info.iconColour,
        icon_url: info.iconUrl
      })),
      extra_info: subject.extraInfo
    }))
  };
};

const feedRowType = {
  COMBAT: 'combat',
  DYM: 'dym',
  RELATED_SEARCHES: 'related_searches'
};

const processHomeFeedData = (homeFeed, goalUID, isValidating) => {
  const newData = homeFeed || {};
  newData.results = (newData?.results || [])
    .map((feed) => {
      return {
        ...feed,
        type: feed.type || feed.blockType,
        extraBlockInfo: feed.extraBlockInfo,
        extraBlockinfo: {
          ...(feed.extraBlockInfo || {}),
          entity_type: feed.extraBlockInfo?.entityType
        },
        label:
          feed.label ||
          feed.title ||
          feed.extraBlockInfo?.label ||
          feed.extraBlockInfo?.title ||
          feed.extraBlockInfo?.name ||
          feed.extraBlockInfo?.entityType,
        key: `${goalUID}_plus`
      };
    })
    .map((feed) => {
      const entityType = feed?.extraBlockInfo?.entityType;
      if (isTestSeriesFeed(entityType)) {
        return getProcessTesTestSeriesFeedData(feed);
      }
      if (isCourseFeedBlock(entityType)) {
        return getProcessClassFeedData(feed);
      }
      if (isEducatorFeed(entityType)) {
        return processEducators(feed);
      }
      if (isTopicFeed(entityType)) {
        return getFormatTopology(feed);
      }
      if (feed.type === feedRowType.DYM) {
        return getProcessedDymTypeFeed(feed);
      }
      if (feed.type === feedRowType.RELATED_SEARCHES) {
        return getProcessedRelatedSearchesTypeFeed(feed);
      }
      if (isBatchFeed(entityType)) {
        return getProcessedBatchFeed(feed);
      }
      if (isBannerBlock(feed.type)) {
        return getCarouselFeed(feed);
      }
      if (feed.type === feedRowType.COMBAT) {
        return getProcessedCombatTypeFeed(feed);
      }
      if (isExternalSubscription(feed.type)) {
        return getProcessedExternalSubscription(feed);
      }
      if (isSubjectFeed(entityType)) {
        return getProcessedSubjects(feed);
      }
      return feed;
    });
  newData.isFetching = isValidating;
  return newData;
};
export const getKey = (goalUID) => {
  return `v1/uplus/subscription/home/?goal_uid=${goalUID}&v=10`;
};

const useHomeFeed = (goalUID, options = { fallbackData: undefined }) => {
  const key = getKey(goalUID);
  const {
    data: homeFeed,
    isValidating,
    isError,
    ...rest
  } = useInfiniteFetch(
    key,
    {
      applyOffsetsAutomatically: false,
      useCamelCase: true
    },
    {
      revalidateFirstPage: false,
      fallbackData: options.fallbackData
    }
  );

  if (options.selector && typeof options.selector === 'function')
    return options.selector(homeFeed || {});

  return {
    key,
    homeFeed: processHomeFeedData(homeFeed, goalUID, isValidating),
    isValidating,
    ...rest
  };
};

export default useHomeFeed;
